import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  loadSession,
  logout as apiLogout,
  saveSession,
  type SessionUser,
} from '../lib/api';
import { useCognitoLogout } from './cognitoLogout';

interface AuthContextValue {
  user: SessionUser | null;
  setCognitoSession: (user: SessionUser) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(() => loadSession());

  const setCognitoSession = useCallback((session: SessionUser) => {
    saveSession(session);
    setUser(session);
  }, []);

  const logout = useCallback(() => {
    apiLogout();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, setCognitoSession, logout }),
    [user, setCognitoSession, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth fuera de AuthProvider');
  return ctx;
}

/** Cognito signOut when available. */
export function usePortalLogout() {
  const { logout } = useAuth();
  const cognitoLogout = useCognitoLogout();
  return useCallback(async () => {
    if (cognitoLogout) await cognitoLogout();
    else logout();
  }, [cognitoLogout, logout]);
}
